// Safety controls for comments system
export interface SafetyConfig {
  maxCommentsPerHour: number;
  maxCommentsPerDay: number;
  minCommentLength: number;
  maxCommentLength: number;
  minTimeBetweenComments: number; // in milliseconds
  enableContentFilter: boolean;
  enableRateLimit: boolean;
  enableCAPTCHA: boolean;
  suspiciousActivityThreshold: number;
}

export interface CommentSafetyCheck {
  isAllowed: boolean;
  reason?: string;
  requiresCAPTCHA?: boolean;
  cooldownTime?: number;
}

export interface UserActivity {
  userId: string;
  commentCount: number;
  lastCommentTime: number;
  suspiciousActivityCount: number;
  isBlocked: boolean;
  blockReason?: string;
  blockExpiry?: number;
}

// Default safety configuration
export const DEFAULT_SAFETY_CONFIG: SafetyConfig = {
  maxCommentsPerHour: 10,
  maxCommentsPerDay: 50,
  minCommentLength: 3,
  maxCommentLength: 1000,
  minTimeBetweenComments: 30000, // 30 seconds
  enableContentFilter: true,
  enableRateLimit: true,
  enableCAPTCHA: true,
  suspiciousActivityThreshold: 3
};

// Inappropriate content patterns
const INAPPROPRIATE_PATTERNS = [
  // Profanity and offensive language
  /\b(fuck|shit|damn|hell|bitch|asshole|bastard|cunt|piss|fag|nigger|retard|idiot|stupid|moron)\b/gi,
  
  // Spam patterns
  /\b(buy|sell|free|money|cash|win|prize|offer|deal|discount|promo|click here|visit now|limited time)\b/gi,
  
  // URL patterns (potential spam)
  /(www\.|http|\.com|\.org|\.net|\.co|bit\.ly|tinyurl|goo\.gl)/gi,
  
  // Excessive repetition
  /(.)\1{4,}/g, // Same character repeated 5+ times
  
  // Excessive punctuation
  /[!@#$%^&*()_+={}[\]|\\:";'<>?,./]{3,}/g,
  
  // All caps (excessive shouting)
  /^[A-Z\s]{20,}$/,
  
  // Phone numbers and emails (potential spam)
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g
];

// Suspicious patterns that might indicate bot behavior
const SUSPICIOUS_PATTERNS = [
  // Random character sequences
  /[a-z]{2}\d{2}[a-z]{2}\d{2}/gi,
  
  // Repeated words
  /\b(\w+)\s+\1\s+\1\b/gi,
  
  // Excessive numbers
  /\d{5,}/g,
  
  // Non-English characters (potential spam)
  /[^\x20-\x7E]/g
];

export class CommentSafetyService {
  private config: SafetyConfig;
  private userActivity: Map<string, UserActivity> = new Map();
  private blockedUsers: Set<string> = new Set();

  constructor(config: SafetyConfig = DEFAULT_SAFETY_CONFIG) {
    this.config = config;
    this.loadUserActivity();
  }

  // Check if a comment is safe to post
  async checkCommentSafety(
    userId: string, 
    content: string, 
    userRole?: string
  ): Promise<CommentSafetyCheck> {
    const now = Date.now();
    const userActivity = this.getUserActivity(userId);

    // Check if user is blocked
    if (userActivity.isBlocked) {
      if (userActivity.blockExpiry && now < userActivity.blockExpiry) {
        return {
          isAllowed: false,
          reason: `Account temporarily blocked: ${userActivity.blockReason}`,
          cooldownTime: userActivity.blockExpiry - now
        };
      } else {
        // Unblock user if expiry has passed
        userActivity.isBlocked = false;
        userActivity.blockReason = undefined;
        userActivity.blockExpiry = undefined;
      }
    }

    // Admin bypass for most restrictions
    const isAdmin = userRole === 'super_admin';
    
    if (!isAdmin) {
      // Rate limiting checks
      if (this.config.enableRateLimit) {
        const rateLimitCheck = this.checkRateLimit(userActivity, now);
        if (!rateLimitCheck.isAllowed) {
          return rateLimitCheck;
        }
      }

      // Time between comments check
      if (now - userActivity.lastCommentTime < this.config.minTimeBetweenComments) {
        const cooldownTime = this.config.minTimeBetweenComments - (now - userActivity.lastCommentTime);
        return {
          isAllowed: false,
          reason: `Please wait ${Math.ceil(cooldownTime / 1000)} seconds before posting another comment`,
          cooldownTime
        };
      }
    }

    // Content filtering
    if (this.config.enableContentFilter) {
      const contentCheck = this.checkContent(content);
      if (!contentCheck.isAllowed) {
        // Increment suspicious activity
        userActivity.suspiciousActivityCount++;
        
        // Block user if too many violations
        if (userActivity.suspiciousActivityCount >= this.config.suspiciousActivityThreshold) {
          this.blockUser(userId, 'Multiple content policy violations', 24 * 60 * 60 * 1000); // 24 hours
          return {
            isAllowed: false,
            reason: 'Account temporarily blocked due to multiple policy violations'
          };
        }
        
        return contentCheck;
      }
    }

    // Check for suspicious patterns
    const suspiciousCheck = this.checkSuspiciousPatterns(content);
    if (suspiciousCheck.requiresCAPTCHA) {
      return {
        isAllowed: true,
        requiresCAPTCHA: true,
        reason: 'Please complete CAPTCHA verification'
      };
    }

    return { isAllowed: true };
  }

  // Check rate limiting
  private checkRateLimit(userActivity: UserActivity, now: number): CommentSafetyCheck {
    const oneHourAgo = now - (60 * 60 * 1000);

    // Reset hourly count if more than an hour has passed
    if (userActivity.lastCommentTime < oneHourAgo) {
      userActivity.commentCount = 0;
    }

    // Check hourly limit
    if (userActivity.commentCount >= this.config.maxCommentsPerHour) {
      const nextAllowedTime = userActivity.lastCommentTime + (60 * 60 * 1000);
      return {
        isAllowed: false,
        reason: `Rate limit exceeded. You can post again in ${Math.ceil((nextAllowedTime - now) / 60000)} minutes`,
        cooldownTime: nextAllowedTime - now
      };
    }

    // Check daily limit (simplified - would need more sophisticated tracking in production)
    if (userActivity.commentCount >= this.config.maxCommentsPerDay) {
      return {
        isAllowed: false,
        reason: 'Daily comment limit exceeded. Please try again tomorrow.'
      };
    }

    return { isAllowed: true };
  }

  // Check content for inappropriate language
  private checkContent(content: string): CommentSafetyCheck {
    const trimmedContent = content.trim();

    // Check minimum length
    if (trimmedContent.length < this.config.minCommentLength) {
      return {
        isAllowed: false,
        reason: `Comment must be at least ${this.config.minCommentLength} characters long`
      };
    }

    // Check maximum length
    if (trimmedContent.length > this.config.maxCommentLength) {
      return {
        isAllowed: false,
        reason: `Comment must be no more than ${this.config.maxCommentLength} characters long`
      };
    }

    // Check for inappropriate patterns
    for (const pattern of INAPPROPRIATE_PATTERNS) {
      if (pattern.test(content)) {
        return {
          isAllowed: false,
          reason: 'Comment contains inappropriate content. Please revise your message.'
        };
      }
    }

    return { isAllowed: true };
  }

  // Check for suspicious patterns that might indicate bot behavior
  private checkSuspiciousPatterns(content: string): { requiresCAPTCHA: boolean } {
    let suspiciousCount = 0;

    for (const pattern of SUSPICIOUS_PATTERNS) {
      if (pattern.test(content)) {
        suspiciousCount++;
      }
    }

    return {
      requiresCAPTCHA: suspiciousCount >= 2 && this.config.enableCAPTCHA
    };
  }

  // Record successful comment
  recordComment(userId: string): void {
    const userActivity = this.getUserActivity(userId);
    const now = Date.now();
    
    userActivity.commentCount++;
    userActivity.lastCommentTime = now;
    
    // Reset suspicious activity count after successful comment
    if (userActivity.suspiciousActivityCount > 0) {
      userActivity.suspiciousActivityCount = Math.max(0, userActivity.suspiciousActivityCount - 1);
    }
    
    this.saveUserActivity();
  }

  // Block a user
  blockUser(userId: string, reason: string, durationMs: number): void {
    const userActivity = this.getUserActivity(userId);
    userActivity.isBlocked = true;
    userActivity.blockReason = reason;
    userActivity.blockExpiry = Date.now() + durationMs;
    
    this.blockedUsers.add(userId);
    this.saveUserActivity();
  }

  // Unblock a user
  unblockUser(userId: string): void {
    const userActivity = this.getUserActivity(userId);
    userActivity.isBlocked = false;
    userActivity.blockReason = undefined;
    userActivity.blockExpiry = undefined;
    userActivity.suspiciousActivityCount = 0;
    
    this.blockedUsers.delete(userId);
    this.saveUserActivity();
  }

  // Get user activity
  private getUserActivity(userId: string): UserActivity {
    if (!this.userActivity.has(userId)) {
      this.userActivity.set(userId, {
        userId,
        commentCount: 0,
        lastCommentTime: 0,
        suspiciousActivityCount: 0,
        isBlocked: false
      });
    }
    return this.userActivity.get(userId)!;
  }

  // Load user activity from localStorage (in production, this would be from a database)
  private loadUserActivity(): void {
    try {
      const stored = localStorage.getItem('commentSafety_userActivity');
      if (stored) {
        const data = JSON.parse(stored);
        this.userActivity = new Map(data);
      }
    } catch (error) {
      console.error('Error loading user activity:', error);
    }
  }

  // Save user activity to localStorage (in production, this would be to a database)
  private saveUserActivity(): void {
    try {
      const data = Array.from(this.userActivity.entries());
      localStorage.setItem('commentSafety_userActivity', JSON.stringify(data));
    } catch (error) {
      console.error('Error saving user activity:', error);
    }
  }

  // Get safety statistics for admin dashboard
  getSafetyStats(): {
    totalUsers: number;
    blockedUsers: number;
    suspiciousUsers: number;
    recentActivity: UserActivity[];
  } {
    const users = Array.from(this.userActivity.values());
    const now = Date.now();
    
    return {
      totalUsers: users.length,
      blockedUsers: users.filter(u => u.isBlocked).length,
      suspiciousUsers: users.filter(u => u.suspiciousActivityCount > 0).length,
      recentActivity: users
        .filter(u => now - u.lastCommentTime < 24 * 60 * 60 * 1000) // Last 24 hours
        .sort((a, b) => b.lastCommentTime - a.lastCommentTime)
        .slice(0, 10)
    };
  }
}

// Export singleton instance
export const commentSafetyService = new CommentSafetyService();
